import {
  ActionFunctionArgs, Form, redirect, useLoaderData, useLocation,
} from 'react-router-dom';
import {
  ChangeEvent, useEffect, useMemo, useState,
} from 'react';
import { today, getLocalTimeZone } from '@internationalized/date';
import { DateValue } from 'react-aria-components';
import AdvancedInput from '../components/form-inputs/advancedInput';
import SplitLayout from '../components/splitLayout';
import { createCampaign } from '../utils/campaign';
import customIcons from '../utils/icons';
import Steps from '../components/campaigns/steps';
import campaignSteps, { previewLetterStep } from '../utils/campaignSteps';
import sanitize from '../utils/formatters';
import useCampaignStore from '../components/campaigns/campaignStore';
import AdvancedCheckbox from '../components/form-inputs/advancedCheckbox';
import { Channel, CampaignType, FilterModalType } from '../types/enums';
import useRequestState from '../hooks/useRequestState';
import PostEligibleHouseholdsPii from '../services/adapters/eligibleHouseholds';
import { CampaignSelection } from '../types/campaigns.type';
import GetLatestSnapshot from '../services/adapters/snapshotResponse';
import useFilterStore from '../components/streetview/filterStore';
import Button from '../components/buttons/baseButton';
import { Snapshot } from '../types/core.type';
import InputDate from '../components/form-inputs/inputDate';
import { useFilterModal } from '../components/modalProvider';
import { getFormString, getRequiredFormString } from '../shared/utils/forms/formData';
import useFilterCount from '../shared/hooks/useFilterCount';
import { hasLockedFilters } from '../features/campaigns';

export function loader() {
  const snapshotAdapter = new GetLatestSnapshot();
  return snapshotAdapter.adaptResponse();
}

export async function action({ request, params }: ActionFunctionArgs) {
  const formData = await request.formData();
  const campaignInformation: CampaignSelection = {
    snapshot: getRequiredFormString(formData, 'snapshot'),
    campaignType: getRequiredFormString(formData, 'campaignType'),
    campaignName: getRequiredFormString(formData, 'campaignName'),
    communicationChannel: getRequiredFormString(formData, 'communicationChannel'),
    excludeHouseholds: formData.has('excludeHouseholds'),
    excludeHouseholdsByMonth: getFormString(formData, 'excludeHouseholdsByMonth') || null,
    stagger: formData.has('staggerCampaign'),
    dispatchDate: getFormString(formData, 'dispatchDate'),
  };
  const newCampaign = await createCampaign(campaignInformation);
  if (params.impactTheme) {
    return redirect(`/campaigns/${params.impactTheme}/new/${newCampaign.id}`);
  }
  return redirect(`/campaigns/new/${newCampaign.id}`);
}

export default function NewCampaign() {
  const snapshot = useLoaderData<Snapshot>();
  const selectedCampaign = useCampaignStore((state) => state.selected);
  const setSelected = useCampaignStore((state) => state.setSelected);
  const [dispatchDateValue, setDispatchDateValue] = useState<DateValue | null>(null);
  const { openFilterModal, closeModal } = useFilterModal();
  // Set when the campaign is started from a household card, so its filters can't be changed here
  const location = useLocation();
  const shouldLockFilters = hasLockedFilters(location.state);

  const selectedFilters = useFilterStore((state) => state.selectedFilters);
  const filterCount = useFilterCount();

  const displayComponent = false;
  const minimumSendDate = today(getLocalTimeZone()).add({ days: 1 });
  const maximumSendDate = minimumSendDate.add({ days: 28 });
  const isDispatchDateValid = selectedCampaign.communicationChannel === Channel.Letter ? !!dispatchDateValue
  && dispatchDateValue.compare(minimumSendDate) >= 0
  && dispatchDateValue.compare(maximumSendDate) <= 0 : true;

  const {
    data: eligibleHouseholds,
    error: householdError,
    handleRequest,
    FallbackWrapper,
  } = useRequestState<number>();

  const hasFilters = Object.keys(selectedFilters).length > 0;
  const effectiveCampaignType = selectedCampaign.campaignType || (hasFilters ? CampaignType.Custom : '');

  const eligibilityParams = useMemo(() => {
    const params = {
      campaignName: selectedCampaign.campaignName,
      snapshot: snapshot.rawSnapshot,
      campaignType: effectiveCampaignType,
      communicationChannel: selectedCampaign.communicationChannel,
      stagger: selectedCampaign.stagger,
      excludeHouseholds: selectedCampaign.excludeHouseholds,
      isComplete: Boolean(
        selectedCampaign.campaignName && effectiveCampaignType && selectedCampaign.communicationChannel,
      ),
      dispatchDate: selectedCampaign.dispatchDate,
    };
    return params;
  }, [
    selectedCampaign.campaignName,
    effectiveCampaignType,
    snapshot,
    selectedCampaign.communicationChannel,
    selectedCampaign.stagger,
    selectedCampaign.excludeHouseholds,
    selectedCampaign.dispatchDate,
  ]);

  useEffect(() => {
    if (!eligibilityParams.isComplete) return;

    const loadEligibleHouseholds = async (params: CampaignSelection) => {
      const eligibleHouseholdsInstance = new PostEligibleHouseholdsPii();
      const eligibleHouseholdsWithPii = eligibleHouseholdsInstance.adaptResponse({ campaignDetails: params, selectedFilters });
      await handleRequest(eligibleHouseholdsWithPii);
    };
    loadEligibleHouseholds(eligibilityParams);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, eligibilityParams]);

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelected(event.target.name, event.target.checked);
  };

  const handleValueChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (event.target.value === 'default') return;
    setSelected(event.target.name, sanitize(event.target.value));
  };

  const baseCampaignDropdownData = ['Pension Credit', 'Healthy Start', 'Attendance Allowance'];
  const campaignDropdownData = hasFilters ? [...baseCampaignDropdownData, CampaignType.Custom] : baseCampaignDropdownData;

  const channelDropdownData = {
    id: 'Channel',
    options: [Channel.Letter, Channel.SMS],
  };

  const titleObject = {
    title: 'Campaign Manager',
    icon: customIcons.sendIcon,
  };

  const checkboxOptions = {
    id: 'excludeHouseholds',
    label: 'Would you like to exclude households that have recently been contacted?',
    text: 'Exclude Households',
  };
  const months = [...Array(24).keys()].map((month) => (month >= 1 ? `${month + 1} Months` : 'Month'));
  const excludeHouseholdsByMonth = {
    id: 'excludeHouseholdsByMonth',
    options: months,
  };

  const showFilterModal = () => {
    openFilterModal({
      mode: shouldLockFilters ? FilterModalType.Summary : FilterModalType.Edit,
      isOpen: true,
      onClose: closeModal,
    });
  };
  const steps = [...campaignSteps, previewLetterStep];
  const eligibleHouseholdMessage = `${eligibleHouseholds} households eligible to receive ${selectedCampaign.communicationChannel} campaign`;
  const campaignNameGuidance = `Choose campaign names that are clear, relevant, and easy for your team to find.
  Include key details such as benefit, timeframe, creator, or target recipients to ensure the name reflects the purpose and focus of the campaign`;
  return (
    <div className="flex flex-col w-full p-4 overflow-auto">
      <h1 className="m-2 p-4 text-header text-4xl font-medium text-primary-dark border-b-2">Create new campaign</h1>
      <SplitLayout leftWeight="" rightWeight="">
        <div className="flex flex-col justify-between items-center w-full h-full overflow-auto">
          <Steps steps={steps} titleObject={titleObject} selected={selectedCampaign} />
          {filterCount > 0 ? (
            <Button
              id="review-filters"
              onClick={showFilterModal}
              label={shouldLockFilters ? 'View Selected Filters' : 'Review Selected Filters'}
              className="bg-primary text-white rounded-md w-fit hover:bg-primary-dark"
            />
          ) : <span className="font-thin">No Filters Selected</span>}
        </div>
        <div className="w-full h-full mx-2 p-2 flex flex-col overflow-auto">
          <h2 className="text-xl font-medium text-left">Campaign Details</h2>
          <h3 className="text-sm font-normal ml-2">
            Running a campaign will use your latest data from LIFT:
            <strong>
              {snapshot.formattedSnapshot}
            </strong>
          </h3>
          <Form id="create-campaign" className="w-full" method="post">
            <div className="flex flex-row space-x-2 items-center">
              <AdvancedInput
                type="text"
                label="Campaign Name"
                name="campaignName"
                id="campaignName"
                value={selectedCampaign.campaignName}
                onChange={handleValueChange}
                tooltip={campaignNameGuidance}
                required
              />
            </div>
            <AdvancedInput
              type="dropdown"
              label="Campaign Type"
              name="campaignType"
              id="campaignType"
              options={campaignDropdownData}
              value={effectiveCampaignType}
              onChange={handleValueChange}
              required
            />
            <AdvancedInput
              type="dropdown"
              label="Communication Channel"
              {...channelDropdownData}
              name="communicationChannel"
              id="communicationChannel"
              onChange={handleValueChange}
              value={selectedCampaign.communicationChannel}
              required
            />
            <AdvancedCheckbox
              fieldsetId="staggerCampaign"
              fieldsetTitle="Would you like to stagger your campaign?"
              options="Stagger Campaign"
              onChange={handleCheckboxChange}
              selected={selectedCampaign.stagger}
              infoBubble={
                'If you choose to stagger your campaign, your list of households will be split into groups depending on the campaign type '
                + 'and communication will be sent 2 weeks apart.'
              }
            />
            <input type="hidden" name="snapshot" value={snapshot.rawSnapshot} />
            {selectedCampaign.communicationChannel === Channel.Letter ? (
              <InputDate
                name="dispatchDate"
                value={dispatchDateValue}
                onChange={setDispatchDateValue}
                minimumDate={minimumSendDate}
                maximumDate={maximumSendDate}
              />
            ) : null}
            {displayComponent ? (
              <AdvancedCheckbox
                fieldsetId="excludeHouseholds"
                fieldsetTitle={checkboxOptions.label}
                options="Exclude Households"
                onChange={handleCheckboxChange}
                selected={selectedCampaign.excludeHouseholds}
              />
            ) : ''}

            {selectedCampaign.excludeHouseholds && (
            <AdvancedInput
              type="dropdown"
              label="For the previous"
              {...excludeHouseholdsByMonth}
              name="excludeHouseholds"
              id="excludeHouseholdsByMonth"
              onChange={handleValueChange}
              required
            />
            )}
          </Form>
          {eligibleHouseholds && !householdError
            ? <p className="mt-auto ml-auto mx-2 font-thin text-sm">{eligibleHouseholdMessage}</p>
            : ''}
        </div>
      </SplitLayout>
      <div className="w-full mt-auto mb-6 pr-4 pt-4 flex justify-center text-center">
        <FallbackWrapper
          loadingMessage="Loading eligible households..."
        >
          <button
            type="submit"
            form="create-campaign"
            className={`${!eligibleHouseholds || !isDispatchDateValid
              ? 'button-base button-default cursor-not-allowed'
              : 'button-base button-green'} w-2/3 md:w-1/3`}
            disabled={!eligibleHouseholds || !isDispatchDateValid}
          >
            Next
          </button>
        </FallbackWrapper>
      </div>
    </div>
  );
}
